import { useEffect, useState } from "react";
import {
  CLIENT_COLOR_LABELS,
  CLIENT_COLORS,
  formatMoney,
  type Client,
  type OpenDeployment,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { calendarDay } from "../lib/stockFormat.js";
import { BillingPill } from "./Stock.js";
import { ClientTag } from "../components/ClientTag.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Menu, MenuItem } from "@vcnet/ui";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { useToast } from "../components/Toast.js";

/**
 * The companies and sites stock goes out to. Their own list rather than a
 * branch of the location tree: a shelf is where something is kept, a client
 * is who it was billed to, and the export needs the second spelled the way
 * the billing system spells it -- which is why renaming is here and matters.
 *
 * A client with nothing ever deployed to it can be deleted. One with history
 * cannot, because last month's invoice names it.
 *
 * Each can wear a colour, picked from a fixed palette in its row's menu, and
 * the tag it makes is how the client is named everywhere else in a stockroom.
 */
export function Clients(props: {
  id: string | null;
  onOpen: (id: string) => void;
  onItem: (itemId: string) => void;
  onBack: () => void;
}) {
  return props.id ? (
    <ClientDetail id={props.id} onItem={props.onItem} onBack={props.onBack} />
  ) : (
    <ClientList onOpen={props.onOpen} />
  );
}

const outThere = (units: number): string =>
  units > 0 ? `${plural(units, "unit")} out there now` : "Nothing out there now";

/**
 * One client and what is out there. Read from the stock overview the Stock
 * screen already loads, so it is the same numbers by construction; a client's
 * closed deployments (its history) are not in that overview and wait for
 * a reason to add an endpoint.
 */
function ClientDetail({
  id,
  onItem,
  onBack,
}: {
  id: string;
  onItem: (itemId: string) => void;
  onBack: () => void;
}) {
  const [data, setData] = useState<{ client: Client | null; lines: OpenDeployment[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void Promise.all([api.clients.list(), api.stock.overview()])
      .then(([{ clients }, overview]) => {
        if (!live) return;
        setData({
          client: clients.find((client) => client.id === id) ?? null,
          lines: overview.open.filter((line) => line.clientId === id),
        });
      })
      .catch(() => {
        if (live) setError("Could not load this client.");
      });
    return () => {
      live = false;
    };
  }, [id]);

  const back = { label: "Clients", onClick: onBack };
  if (error) {
    return (
      <>
        <PageHeader title="Client" back={back} />
        <Notice tone="error">{error}</Notice>
      </>
    );
  }
  if (data === null) {
    return (
      <>
        <PageHeader title="Client" back={back} />
        <Panel>
          <div className="skeleton-line" />
        </Panel>
      </>
    );
  }
  if (data.client === null) {
    return (
      <>
        <PageHeader title="Client" back={back} />
        <Panel>
          <EmptyState icon="globe" title="That client is not on the list.">
            It may have been deleted.
          </EmptyState>
        </Panel>
      </>
    );
  }

  const { client, lines } = data;
  const waiting = lines.filter((line) => line.billable && !line.billedAt).length;
  return (
    <>
      <PageHeader
        title={client.name}
        back={back}
        meta={`${outThere(client.deployedUnits)}${waiting ? ` · ${plural(waiting, "line")} waiting to be billed` : ""}`}
      />
      <Panel>
        {lines.length === 0 ? (
          <EmptyState icon="external" title="Nothing is out at this client." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Serial</th>
                <th>Where</th>
                <th>Out</th>
                <th>Price</th>
                <th>Went out</th>
                <th>Billing</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td data-label="Item">
                    <button type="button" className="link" onClick={() => onItem(line.itemId)}>
                      {line.title || "Untitled item"}
                    </button>
                  </td>
                  <td data-label="Serial" className="mono">
                    {line.serial || <span className="faint">none</span>}
                  </td>
                  <td data-label="Where">{line.spot || <span className="faint">not said</span>}</td>
                  <td data-label="Out">{line.quantity - line.returned}</td>
                  <td data-label="Price">{formatMoney(line.unitPriceCents)}</td>
                  <td data-label="Went out">{calendarDay(line.deployedOn)}</td>
                  <td data-label="Billing">
                    <BillingPill billable={line.billable} billedAt={line.billedAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function ClientList({ onOpen }: { onOpen: (id: string) => void }) {
  const [clients, setClients] = useState<Client[] | null>(null);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let live = true;
    void api.clients
      .list()
      .then(({ clients }) => {
        if (live) setClients(clients);
      })
      .catch(() => {
        if (live) setError("Could not load clients.");
      });
    return () => {
      live = false;
    };
  }, []);

  async function run(
    key: string,
    work: () => Promise<{ clients: Client[] }>,
    done: string,
  ): Promise<boolean> {
    setBusy(key);
    setError(null);
    try {
      setClients((await work()).clients);
      toast(done);
      return true;
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    if (await run("add", () => api.clients.create(name.trim()), "Added.")) setName("");
  }

  async function rename(event: React.FormEvent) {
    event.preventDefault();
    if (!editing?.name.trim()) return;
    const { id, name: next } = editing;
    if (await run(id, () => api.clients.rename(id, next.trim()), "Renamed.")) setEditing(null);
  }

  return (
    <>
      <PageHeader title="Clients" />

      <Panel note="Spell each one the way your billing system does. The export uses these names as they are.">
        <form className="inline-add" onSubmit={add}>
          <Icon name="globe" size={18} />
          <input
            className="input"
            aria-label="A new client's name"
            placeholder="Acme Ltd, Leeds office"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <button className="button" type="submit" disabled={busy === "add" || !name.trim()}>
            Add
          </button>
        </form>
        {error && <Notice tone="error">{error}</Notice>}
      </Panel>

      <Panel>
        {clients === null ? (
          <div className="skeleton-line" />
        ) : clients.length === 0 ? (
          <EmptyState icon="globe" title="No clients yet.">
            Add the companies or sites you deploy stock to.
          </EmptyState>
        ) : (
          <ul className="rows">
            {clients.map((client) =>
              editing?.id === client.id ? (
                <li key={client.id} className="row-item">
                  <form className="inline-add client-rename" onSubmit={rename}>
                    <input
                      className="input"
                      aria-label={`New name for ${client.name}`}
                      value={editing.name}
                      autoFocus
                      onChange={(event) => setEditing({ id: client.id, name: event.target.value })}
                    />
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="button button-small" disabled={busy === client.id}>
                      Save
                    </button>
                  </form>
                </li>
              ) : (
                <li key={client.id} className="row-item">
                  <button
                    type="button"
                    className="row-link"
                    aria-label={`Open ${client.name}`}
                    onClick={() => onOpen(client.id)}
                  >
                    <span className="row-text">
                      <ClientTag name={client.name} color={client.color} />
                      <span className="muted small">{outThere(client.deployedUnits)}</span>
                    </span>
                    <Icon name="chevron-right" size={16} />
                  </button>
                  <Menu
                    label={`Actions for ${client.name}`}
                    buttonClassName="icon-button icon-button-bordered"
                    button={<Icon name="more" size={20} />}
                  >
                    {(close) => (
                      <>
                        <p className="filter-menu-title">Colour</p>
                        <div
                          className="client-swatches"
                          role="group"
                          aria-label={`Colour for ${client.name}`}
                        >
                          {[null, ...CLIENT_COLORS].map((color) => (
                            <button
                              key={color ?? "plain"}
                              type="button"
                              className="client-swatch"
                              data-color={color ?? undefined}
                              aria-label={color ? CLIENT_COLOR_LABELS[color] : "Plain grey"}
                              title={color ? CLIENT_COLOR_LABELS[color] : "Plain grey"}
                              aria-pressed={client.color === color}
                              disabled={busy === client.id}
                              onClick={() => {
                                close();
                                if (client.color !== color) {
                                  void run(
                                    client.id,
                                    () => api.clients.recolor(client.id, color),
                                    color
                                      ? `${client.name} is ${CLIENT_COLOR_LABELS[color].toLowerCase()}.`
                                      : `${client.name} is plain again.`,
                                  );
                                }
                              }}
                            >
                              <span className="client-tag-dot" aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                        <MenuItem
                          icon={<Icon name="pencil" />}
                          onClick={() => {
                            close();
                            setEditing({ id: client.id, name: client.name });
                          }}
                        >
                          Rename
                        </MenuItem>
                        <MenuItem
                          danger
                          icon={<Icon name="trash" />}
                          disabled={busy === client.id}
                          onClick={() => {
                            close();
                            void run(client.id, () => api.clients.remove(client.id), "Deleted.");
                          }}
                        >
                          Delete
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </li>
              ),
            )}
          </ul>
        )}
      </Panel>
    </>
  );
}
